import moment from 'moment';
import i18n from 'i18next';
import { DateFormat } from 'utils/constants';
import { ReportEntity, ReportsFilter } from 'features/reporting/thunks/types';
import { UnitType } from 'utils/characteristic';

const { PERIOD_TIME_FORMAT, REST_DATE_FORMAT, YEAR_MONTH_FORMAT } = DateFormat;

export enum BenefitEvaluation {
  ECONOMIC_BENEFIT_EVALUATION_HOUR = 'ECONOMIC_BENEFIT_EVALUATION_HOUR',
  ECONOMIC_BENEFIT_EVALUATION_PERIOD = 'ECONOMIC_BENEFIT_EVALUATION_PERIOD',
  ECONOMIC_BENEFIT_EVALUATION_DAY = 'ECONOMIC_BENEFIT_EVALUATION_DAY',
  ECONOMIC_BENEFIT_EVALUATION_MONTH = 'ECONOMIC_BENEFIT_EVALUATION_MONTH'
}

export enum AggregationFn {
  MEAN = 'MEAN',
  CUMULATIVE_SUM = 'CUMULATIVE_SUM'
}

export enum BenefitType {
  BENEFITS = 'benefits',
  CUMULATIVE_BENEFITS = 'cumulativeBenefits'
}

export enum BenefitCostType {
  UNIT = 'unit',
  SUMMARY = 'summary'
}

export enum EconomicBenefitReportFields {
  MONTH = 'month',
  MARKET_DAY = 'market_day',
  MARKET_HOUR_START = 'market_hour_start',
  MARKET_HOUR_END = 'market_hour_end',
  MARKET_PERIOD_START = 'market_period_start',
  MARKET_PERIOD_END = 'market_period_end',
  BALANCING_GROUP_ID = 'balancing_group_id',
  POSITIVE_IMBALANCE_MWH = 'positive_imbalance_mwh',
  NEGATIVE_IMBALANCE_MWH = 'negative_imbalance_mwh',
  TOTAL_IMBALANCE_PRICE_EUR = 'total_imbalance_price_eur',
  IMBALANCE_UNIT_PRICE_EUR = 'imbalance_unit_price_eur',
  POSITIVE_IMBALANCE_MODEL_MWH = 'positive_imbalance_model_mwh',
  NEGATIVE_IMBALANCE_MODEL_MWH = 'negative_imbalance_model_mwh',
  TOTAL_IMBALANCE_MODEL_PRICE_EUR = 'total_imbalance_model_price_eur',
  IMBALANCE_MODEL_UNIT_PRICE_EUR = 'imbalance_model_unit_price_eur',
  IMBALANCE_PRICE_DIFFERENCE_EUR = 'imbalance_price_difference_eur',
  IMBALANCE_UNIT_PRICE_DIFFERENCE_EUR = 'imbalance_unit_price_difference_eur',
  RESERVED_CAPACITY_EXCEEDING_DETECTED_COUNT = 'reserved_capacity_exceeding_detected_count',
  RESERVED_CAPACITY_EXCEEDING_DETECTED_MWH = 'reserved_capacity_exceeding_detected_mwh',
  RESERVED_CAPACITY_EXCEEDING_COUNT = 'reserved_capacity_exceeding_count',
  RESERVED_CAPACITY_EXCEEDING_MWH = 'reserved_capacity_exceeding_mwh',
  RESERVED_CAPACITY_EXCEEDING_DETECTED_PRICE_EUR = 'reserved_capacity_exceeding_detected_price_eur',
  RESERVED_CAPACITY_EXCEEDING_PRICE_EUR = 'reserved_capacity_exceeding_price_eur',
  RESERVED_CAPACITY_EXCEEDING_PRICE_DIFFERENCE_EUR = 'reserved_capacity_exceeding_price_difference_eur'
}

export enum AggregationFields {
  TOTAL_IMBALANCE_PRICE_EUR = 'total_imbalance_price_eur',
  IMBALANCE_UNIT_PRICE_EUR = 'imbalance_unit_price_eur',
  POSITIVE_IMBALANCE_MWH = 'positive_imbalance_mwh',
  NEGATIVE_IMBALANCE_MWH = 'negative_imbalance_mwh',
  RESERVED_CAPACITY_EXCEEDING_PRICE_EUR = 'reserved_capacity_exceeding_price_eur',
  TOTAL_IMBALANCE_MODEL_PRICE_EUR = 'total_imbalance_model_price_eur',
  IMBALANCE_MODEL_UNIT_PRICE_EUR = 'imbalance_model_unit_price_eur',
  POSITIVE_IMBALANCE_MODEL_MWH = 'positive_imbalance_model_mwh',
  NEGATIVE_IMBALANCE_MODEL_MWH = 'negative_imbalance_model_mwh',
  RESERVED_CAPACITY_EXCEEDING_DETECTED_PRICE_EUR = 'reserved_capacity_exceeding_detected_price_eur',
  IMBALANCE_PRICE_DIFFERENCE_EUR = 'imbalance_price_difference_eur',
  IMBALANCE_UNIT_PRICE_DIFFERENCE_EUR = 'imbalance_unit_price_difference_eur',
  RESERVED_CAPACITY_EXCEEDING_PRICE_DIFFERENCE_EUR = 'reserved_capacity_exceeding_price_difference_eur',
  RESERVED_CAPACITY_EXCEEDING_DETECTED_MWH = 'reserved_capacity_exceeding_detected_mwh',
  RESERVED_CAPACITY_EXCEEDING_DETECTED_COUNT = 'reserved_capacity_exceeding_detected_count',
  RESERVED_CAPACITY_EXCEEDING_MWH = 'reserved_capacity_exceeding_mwh',
  RESERVED_CAPACITY_EXCEEDING_COUNT = 'reserved_capacity_exceeding_count'
}

export const economicBenefitChartHeight = 200;

export const economicBenefitReportMaxDate = moment()
  .subtract(2, 'days')
  .toISOString();

const formatToYearMonth = (date: string) =>
  moment(date).format(YEAR_MONTH_FORMAT);

export const formatToRestDate = (date: string) =>
  moment(date).format(REST_DATE_FORMAT);

export const formatEvaluationDate = (
  evaluation: BenefitEvaluation,
  date: string
) => {
  return evaluation === BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_MONTH
    ? formatToYearMonth(date)
    : formatToRestDate(date);
};

export const getFieldByEvaluation = (evaluation: BenefitEvaluation) => {
  return evaluation === BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_MONTH
    ? 'month'
    : 'market_day';
};

export const getReportTypeForChartByEvaluation = (
  evaluation: BenefitEvaluation
) => {
  return evaluation === BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_MONTH
    ? BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY
    : BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_PERIOD;
};

export const getValueWithUnit = (
  value: string | number | undefined,
  unit: UnitType
) => {
  if (value == null) return '';
  const unitOfMeasure = i18n.t(`common.unitsOfMeasure.${unit}`) || '';
  return `${value.toString()} ${unitOfMeasure}`;
};

export const getSeparatedValuesWithUnit = (
  values: (string | number | undefined)[],
  unit: UnitType,
  separator = ' / '
) => {
  const existingValues = values.filter(Boolean);
  const unitOfMeasure = i18n.t(`common.unitsOfMeasure.${unit}`) || '';

  if (existingValues.length === 0) return '';
  return `${existingValues.join(separator)} ${unitOfMeasure}`;
};

export const getBenefitTableData = (reportsList: ReportEntity[]) => {
  const row = reportsList.at(0);
  if (row) return row as ReportEntity<EconomicBenefitReportFields>;
  return undefined;
};

export const getFormattedTime = (
  benefitType: BenefitType,
  report: ReportEntity<EconomicBenefitReportFields>,
  aggregation: AggregationFn,
  evaluation: BenefitEvaluation
) => {
  let time = '';
  let format = '';

  if (benefitType === BenefitType.BENEFITS) {
    if (evaluation === BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY) {
      time = `${report.market_day} ${report.market_period_start}`;
      format = `${REST_DATE_FORMAT} ${PERIOD_TIME_FORMAT}`;
    }
    if (evaluation === BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_MONTH) {
      time = report.market_day?.toString() ?? '';
      format = REST_DATE_FORMAT;
    }
  }

  if (benefitType === BenefitType.CUMULATIVE_BENEFITS) {
    if (aggregation === AggregationFn.MEAN) {
      time = `${report.market_day} ${report.market_period_start}`;
      format = `${REST_DATE_FORMAT} ${PERIOD_TIME_FORMAT}`;
    }
    if (aggregation === AggregationFn.CUMULATIVE_SUM) {
      time = report.market_day?.toString() ?? '';
      format = REST_DATE_FORMAT;
    }
  }

  return moment(time, format).valueOf();
};

export const getBalancingGroupField = (id: string): ReportsFilter => ({
  field: 'balancing_group_id',
  value: [id],
  comparison: 'EQUALS',
  operation: 'AND'
});

export const shouldPrefillChartPoints = (
  benefitType: BenefitType,
  evaluation: BenefitEvaluation
) =>
  benefitType === BenefitType.BENEFITS &&
  evaluation === BenefitEvaluation.ECONOMIC_BENEFIT_EVALUATION_DAY;
